import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/ProductLineItemsService';
import { useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useProductLineItems = (productId: string) => {
    if (!productId) {
        throw new Error('productId is required parameter');
    }

    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    
    const sort = { column: 'created', direction: 'desc' as const };
    
    // INFINITE QUERY
    const productLineItemsQuery = useInfiniteQuery({
        queryKey: ['product-line-items', productId, keyword, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ productId, limit, sort, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Product Line Item deleted');
            queryClient.invalidateQueries({ queryKey: ['product-line-items', productId] });
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        if (productLineItemsQuery.hasNextPage) {
            productLineItemsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const handleClearRecords = () => {
        queryClient.removeQueries({ queryKey: ['product-line-items', productId] });
    };

    const allItems = productLineItemsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: productLineItemsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: productLineItemsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        handleClearRecords,
        deleteMutation
    };
};